import FlowchartViewer from '@/components/Flowchart/FlowchartViewer'
import { usePanelMode } from '@/components/ui/Panel'
import { RetryValidationProvider } from '@/contexts/RetryValidationContext'
import { useScrollbarVisibility } from '@/hooks/useScrollbarVisibility'
import { ExecutionService } from '@/lib/features/execution/ExecutionService'
import { filterNodeExecutionsForOutputs } from '@/lib/utils/workflowFilterUtils'
import { motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import Image from 'next/image'
import React, { useEffect, useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import type { WorkflowConfig } from 'sprites-types/workflow/core'
import type { PanelContent } from '../../ui/Panel'
import { WorkflowToolResult } from '../../WorkflowToolResults/WorkflowToolResult'

interface WorkflowExecutionResultsContentProps {
  executionId: string
  onOpenPanel?: (content: PanelContent, breadcrumbs: any[]) => void
}

export function WorkflowExecutionResultsContent({
  executionId,
  onOpenPanel,
}: WorkflowExecutionResultsContentProps) {
  // Get mode from panel context (managed by Panel topbar toggle)
  const { mode } = usePanelMode()
  const [nodeExecutions, setNodeExecutions] = useState<Record<string, NodeExecution> | null>(null)
  const [workflowConfig, setWorkflowConfig] = useState<WorkflowConfig | undefined>(undefined)
  const [workflowRef, setWorkflowRef] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)

  // Handle scrollbar visibility on scroll
  useScrollbarVisibility(scrollContainerRef)

  useEffect(() => {
    const executionService = ExecutionService.getInstance()

    const fetchData = async () => {
      try {
        const executionResponse = await executionService.getExecutionData(executionId)
        setNodeExecutions(executionResponse.nodeExecutions)

        if (executionResponse.executionData.workflowRef) {
          console.log('[WorkflowExecutionResultsContent] Fetching workflow config:', {
            workflowRef: executionResponse.executionData.workflowRef,
            executionId,
          })

          setWorkflowRef(executionResponse.executionData.workflowRef)

          const workflowResponse = await fetch(
            `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/workflows/${executionResponse.executionData.workflowRef}`,
            { method: 'GET', credentials: 'include' }
          )

          if (workflowResponse.ok) {
            const workflowData = await workflowResponse.json()

            if (workflowData.success && workflowData.workflow?.data) {
              setWorkflowConfig(workflowData.workflow.data)
            }
          } else {
            console.error('[WorkflowExecutionResultsContent] Failed to fetch workflow:', {
              status: workflowResponse.status,
              workflowRef: executionResponse.executionData.workflowRef,
            })
          }
        }

        setIsLoading(false)
      } catch (err) {
        console.error('[WorkflowExecutionResultsContent] Failed to fetch execution data:', err)
        setError('Failed to load execution results')
        setIsLoading(false)
      }
    }

    void fetchData()
  }, [executionId])

  if (isLoading) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-white/20">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <div className="text-sm font-medium text-gray-600">Loading execution results...</div>
      </div>
    )
  }

  if (error || !nodeExecutions) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-sm text-red-500">{error || 'No execution data available'}</div>
      </div>
    )
  }

  const nodeExecutionArray = Object.values(nodeExecutions)

  // Filter executions based on workflow outputs
  const outputFilteredExecutions = workflowConfig
    ? filterNodeExecutionsForOutputs(nodeExecutionArray, workflowConfig)
    : nodeExecutionArray

  // Filter out failed/errored executions (same as WorkflowResults does)
  const filteredExecutions = outputFilteredExecutions.filter(
    (exec) => exec.status !== 'failed' && exec.status !== 'errored'
  )

  return (
    <div className="relative h-full w-full overflow-hidden">
      {/* Results Mode */}
      <motion.div
        key="results"
        className="absolute inset-0"
        initial={false}
        animate={{
          opacity: mode === 'results' ? 1 : 0,
          x: mode === 'results' ? 0 : -20,
        }}
        transition={{
          duration: 0.3,
          ease: 'easeInOut',
        }}
        style={{
          pointerEvents: mode === 'results' ? 'auto' : 'none',
          zIndex: mode === 'results' ? 20 : 10,
          visibility: mode === 'results' ? 'visible' : 'hidden',
        }}
      >
        <div
          ref={scrollContainerRef}
          className="scrollbar-auto relative h-full overflow-auto bg-white/20"
        >
          {filteredExecutions.length === 0 ? (
            // Empty state for Results mode only
            <div className="flex h-full min-h-[400px] w-full items-center justify-center">
              <div className="text-center">
                <Image
                  width={200}
                  height={200}
                  alt="No results"
                  src="/images/dashboard/no-result.png"
                  className="mx-auto mb-6"
                />
                <h3 className="mb-2 text-lg font-medium text-gray-900">No results to display</h3>
                <p className="mx-auto max-w-sm text-gray-600">
                  The workflow execution didn&apos;t produce any output results to show here. Check
                  the workflow logs for more details about the execution.
                </p>
              </div>
            </div>
          ) : (
            // Show results when they exist
            <div className="flex w-full flex-col gap-y-4 p-4">
              {filteredExecutions.map((nodeExecution) => (
                <WorkflowToolResult
                  key={nodeExecution.executionId}
                  nodeExecution={nodeExecution}
                  workflowConfig={
                    workflowConfig || {
                      id: 'unknown',
                      name: 'Unknown Workflow',
                      description: '',
                      start: '',
                      nodes: [],
                      parameters: { type: 'object', properties: {}, required: [] },
                      outputs: [],
                    }
                  }
                  onOpenPanel={onOpenPanel}
                />
              ))}
            </div>
          )}
        </div>
      </motion.div>

      {/* Workflow Mode */}
      <motion.div
        key="flow"
        className="absolute inset-0"
        initial={false}
        animate={{
          opacity: mode === 'flow' ? 1 : 0,
          x: mode === 'flow' ? 0 : 20,
        }}
        transition={{
          duration: 0.3,
          ease: 'easeInOut',
        }}
        style={{
          pointerEvents: mode === 'flow' ? 'auto' : 'none',
          zIndex: mode === 'flow' ? 20 : 10,
          visibility: mode === 'flow' ? 'visible' : 'hidden',
        }}
      >
        <div className="relative h-full">
          {workflowRef && nodeExecutions ? (
            <RetryValidationProvider workflowRef={workflowRef} nodeExecutions={nodeExecutions}>
              <FlowchartViewer
                workflowId={workflowRef}
                onOpenPanel={onOpenPanel}
                workflowConfig={workflowConfig}
                nodeExecutions={nodeExecutions}
                workflowExecutionId={executionId}
              />
            </RetryValidationProvider>
          ) : (
            <FlowchartViewer
              workflowId={workflowRef}
              onOpenPanel={onOpenPanel}
              workflowConfig={workflowConfig}
              nodeExecutions={nodeExecutions || undefined}
              workflowExecutionId={executionId}
            />
          )}
        </div>
      </motion.div>
    </div>
  )
}
